import type { AudioDefinition } from '../../sdk/theme';
import type { AudioId } from '../../shared/types/ids';
import { AUDIO_TUNABLES } from '../config/audio';

/**
 * The platform's one sound seam (PS5-2, D-094(3)).
 *
 * **Why it is not Phaser's.** The Phaser host is constructed with `audio: { noAudio: true }` and
 * has been since Phase 0. Sound is not a property of the stage: the shell has grown-up surfaces of
 * its own, a mission is only part of a sitting, and a service that lived inside the game canvas
 * would be built and destroyed with a scene rather than with the sitting. So audio is owned where
 * narration and the camera are owned — by the app shell — and disposed on the same exit paths.
 *
 * **Why it is manifest-driven and nothing else.** Every playable sound is an `AudioDefinition` in
 * the game's theme manifest, addressed by its ID. Nothing above this file may name a file: the
 * same AST-01 discipline the picture assets have had since D-014, applied to the speakers. An ID
 * the manifest does not declare is silence, never an exception — a missing sound must not be able
 * to end a five-year-old's mission.
 *
 * **Nothing here may throw (S0-R1).** Every method is reachable from the mission runtime's event
 * path, and the engine underneath is browser machinery that throws for ordinary reasons. A sound
 * that cannot start is silence; it is never a round. So every engine call is guarded, and a sound
 * the engine refused is reported as **not playing** rather than as a phantom entry that a later
 * `stop` would fail to stop.
 *
 * **What it deliberately does not do.** It does not decide *when* anything plays. Ducking is the
 * only coordination it performs, and it performs it by one rule (see {@link AudioService.claimForeground}).
 */

/**
 * Which of the two things a grown-up controls this sound belongs to (A-R1).
 *
 * `sound` is the music and the chimes: the **Sound** switch and its volume move the master gain,
 * and everything on this bus moves with it. `speech` is a spoken line: it is governed by the
 * *narration* controls instead, so it is routed past the master entirely. Two labelled controls,
 * two buses — a family can have the voice without the music, which is a configuration a single
 * master makes unreachable.
 *
 * Ducking is unaffected either way: that is a claim about one sound relative to another, not about
 * which switch owns it.
 */
export type AudioBus = 'sound' | 'speech';

/** What the engine is asked to start. The service computes it; nothing above ever sees a path. */
export interface AudioPlaybackRequest {
  readonly audioId: AudioId;
  readonly source: string;
  readonly loop: boolean;
  /** 0..1, before the master volume — and *instead of* it on the `speech` bus. */
  readonly gain: number;
  /** Defaults to `sound`: a caller that says nothing is on the switch that says "sound". */
  readonly bus?: AudioBus;
  /** Called by the engine when a non-looping sound reaches its end. */
  readonly onEnded?: () => void;
  /**
   * Called by the engine when a start it had already accepted is refused by the browser (R1-3).
   *
   * `HTMLMediaElement.play()` returns a promise, and it rejects for entirely ordinary reasons — an
   * unsupported codec, a file that is not there, an element the autoplay policy stopped. The graph
   * was built by then, so the synchronous return said nothing was wrong. Without this the service
   * would list a sound nobody can hear for the rest of the sitting, and a baked line would wait out
   * its watchdog instead of falling back to the browser voice.
   *
   * Never called for a sound that was stopped first: a pending `play()` rejects with `AbortError`
   * when the element is paused, and that rejection is the stop's own consequence.
   */
  readonly onRefused?: () => void;
}

/**
 * How a `playToEnd` finished — every way a sound can be over, told apart (R1-3).
 *
 * `ended` means the child heard it through. `stopped` means something cut it short on purpose: an
 * interruption, a replacement, the sitting ending. `refused` means it never made a sound at all,
 * for any reason — muted, locked, undeclared, or a browser that would not play the file.
 *
 * The distinction exists because it changes what a caller should do. Pre-baked narration falls back
 * to the browser voice on `refused` and stays quiet on `stopped`, and it can only do that if the
 * seam stops reporting all three as the same silence (D-095(7): audio never lies about its state).
 */
export type AudioPlaybackEnd = 'ended' | 'stopped' | 'refused';

export interface AudioTrack {
  /** Ramp this sound's own gain to `gain` over `rampMs`. */
  setGain(gain: number, rampMs: number): void;
  /** Play a non-looping sound again from the beginning, rather than layering a second copy. */
  restart(): void;
  stop(): void;
}

/**
 * The narrow slice of a sound engine the service needs.
 *
 * A port, so every decision above can be tested without a browser audio stack, and so the one
 * place that touches that stack is a single implementation file this interface describes
 * (`web-audio-engine.ts`, pinned by `tests/architecture/audio-boundaries.test.ts`).
 */
export interface AudioEngine {
  /**
   * Make the engine capable of sound. Called from the grown-up's start gesture and nowhere else:
   * every browser autoplay policy is satisfied by a user gesture, and inventing a second gesture
   * to satisfy it would be a worse product than riding the one that already exists.
   */
  resume(): Promise<void>;
  /** 0..1, applied to everything at once. */
  setMasterGain(gain: number, rampMs: number): void;
  /**
   * Start a sound, or refuse it by returning `null`.
   *
   * `null` is the whole of the refusal contract at this seam: there is no track, so there is
   * nothing to stop, nothing to list, and nothing for a caller to mistake for a sound (R1-3). An
   * inert track that answered every method with a shrug used to be returned here instead, and it
   * was indistinguishable from one that was playing.
   */
  play(request: AudioPlaybackRequest): AudioTrack | null;
  /** Release every resource. Nothing may make a sound afterwards. */
  close(): void;
}

/**
 * What the readout sees. A snapshot, never a live object: a caller must not be able to hold a
 * reference and watch it change under them.
 */
export interface AudioState {
  readonly unlocked: boolean;
  readonly enabled: boolean;
  readonly volume: number;
  readonly ducked: boolean;
  readonly playing: readonly AudioId[];
}

export type AudioStateListener = (state: AudioState) => void;

export interface AudioService {
  /** True once the grown-up's start gesture has been through {@link unlock}. */
  readonly unlocked: boolean;
  readonly enabled: boolean;
  readonly volume: number;
  /** True while anything is attenuated because something else holds the foreground. */
  readonly ducked: boolean;
  /** The manifest IDs sounding right now, in start order. */
  playing(): readonly AudioId[];
  /**
   * Watch the service's state (the `observeNarration` pattern, applied to sound).
   *
   * Sound leaves no trace an automated browser can see, so the stage renders this into a
   * grown-up-facing diagnostic attribute and the end-to-end suite reads it there. Strictly a
   * readout: nothing decides anything from it, and a listener that throws is the listener's
   * problem — it must never be able to stop a child hearing something.
   */
  subscribe(listener: AudioStateListener): () => void;
  /**
   * The sounds this sitting may play. Replaces whatever was loaded before — a second game's
   * manifest must not leave the first one's IDs playable.
   */
  loadLibrary(audio: Readonly<Record<AudioId, AudioDefinition>>): void;
  /**
   * The IDs the loaded library declares, in manifest order (readout).
   *
   * Exists so a caller can ask *whether this sitting has a particular sound* before deciding what
   * to do instead — which is precisely the question pre-baked narration asks of every line before
   * it falls back to the browser voice (Slice A″). It answers from the library and nothing else:
   * the manifest stays the only truth about what is playable.
   */
  declaredIds(): readonly AudioId[];
  unlock(): Promise<void>;
  play(audioId: AudioId): void;
  /**
   * Play a non-looping sound and resolve when it is over (Slice A″).
   *
   * "Over" means every way a sound can end: the engine reported the end, something stopped it,
   * the same ID was asked for again, or the sitting ended. And a sound that never started — muted,
   * still locked, not declared by this theme, a loop with no end to wait for, an engine that
   * refused — resolves **immediately**, because a caller waiting on silence is the S0-R1 failure
   * wearing a different hat. It never rejects.
   *
   * `gain` attenuates this one line by the caller's own factor, on top of the manifest's level.
   *
   * The resolved {@link AudioPlaybackEnd} says *which* of those happened, because "it played" and
   * "it never made a sound" ask different things of the caller (R1-3).
   */
  playToEnd(audioId: AudioId, options?: { gain?: number }): Promise<AudioPlaybackEnd>;
  stop(audioId: AudioId): void;
  stopAll(): void;
  setEnabled(enabled: boolean): void;
  /** 0..1; anything else is clamped rather than passed on. */
  setVolume(volume: number): void;
  /**
   * Claim the foreground at `priority` until the returned function is called.
   *
   * Every playing sound whose manifest `duckPriority` is **strictly below** the highest live claim
   * is attenuated to {@link AUDIO_TUNABLES.duckedGain} of its own gain, and released when no such
   * claim remains. That is the whole of the ducking contract: one rule, one direction, no
   * per-caller special cases. The returned release is idempotent, so a caller that unwinds twice
   * — a `finally` plus a cancellation, say — cannot release somebody else's claim.
   */
  claimForeground(priority: number): () => void;
  /** End of sitting. Everything stops, the engine is closed, and the service goes inert. */
  dispose(): void;
}

export interface AudioServiceOptions {
  engine: AudioEngine;
  /** Starting master volume; defaults to the flagged gentle default. */
  volume?: number;
  enabled?: boolean;
}

function clampGain(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * A caller's own attenuation, clamped the other way round.
 *
 * {@link clampGain} reads nonsense as silence, which is the right reading for a *level*: an
 * unreadable master volume should not blast a family. It is the wrong reading for an
 * attenuation, where nonsense would silence a line somebody asked to be said — so an unusable
 * factor means "do not attenuate" instead.
 */
function clampScale(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(1, Math.max(0, value));
}

/**
 * Which switch owns this sound (A-R1).
 *
 * Derived from the category rather than from the caller, so there is one answer per sound however
 * it was started: a `narration` entry is speech wherever it is played from.
 */
function busFor(definition: AudioDefinition): AudioBus {
  return definition.category === 'narration' ? 'speech' : 'sound';
}

/** A category's baseline level. The manifest entry's own `volume` scales it. */
function categoryGain(definition: AudioDefinition): number {
  switch (definition.category) {
    case 'music':
      return AUDIO_TUNABLES.musicGain;
    case 'narration':
      return AUDIO_TUNABLES.narrationGain;
    case 'sfx':
      return AUDIO_TUNABLES.sfxGain;
  }
}

interface LiveSound {
  definition: AudioDefinition;
  track: AudioTrack;
  /** The gain it would have if nothing held the foreground. */
  fullGain: number;
  ducked: boolean;
  /**
   * Settles a caller waiting for this sound to be over, if anybody is (`playToEnd`).
   *
   * Held on the sound rather than beside it so that **every** way a sound leaves — ended, stopped,
   * replaced, disposed, refused — goes through the one place that removes it and therefore releases
   * the waiter, carrying the reason with it. A release enumerated at each exit is a release
   * somebody will forget to add to the next exit.
   */
  finish?: (end: AudioPlaybackEnd) => void;
}

export function createAudioService(options: AudioServiceOptions): AudioService {
  const engine = options.engine;
  let library: Readonly<Record<AudioId, AudioDefinition>> = {};
  const live = new Map<AudioId, LiveSound>();
  /** Outstanding foreground claims, newest last. A claim is identified by its own token. */
  const claims: { token: symbol; priority: number }[] = [];

  const listeners = new Set<AudioStateListener>();
  let unlocked = false;
  let disposed = false;
  let enabled = options.enabled ?? true;
  let volume = clampGain(options.volume ?? AUDIO_TUNABLES.defaultSoundVolume);

  const isDucked = (): boolean => [...live.values()].some((sound) => sound.ducked);

  const snapshot = (): AudioState => ({
    unlocked,
    enabled,
    volume,
    ducked: isDucked(),
    playing: [...live.keys()],
  });

  const notify = (): void => {
    const state = snapshot();
    if (listeners.size === 0) return;
    for (const listener of [...listeners]) {
      try {
        listener(state);
      } catch {
        // A readout that throws is a readout problem. It must not silence the game.
      }
    }
  };

  const foregroundPriority = (): number =>
    claims.reduce((highest, claim) => Math.max(highest, claim.priority), Number.NEGATIVE_INFINITY);

  const shouldDuck = (definition: AudioDefinition): boolean =>
    claims.length > 0 && definition.duckPriority < foregroundPriority();

  /** Engine work that is allowed to fail. Failure is silence, never an exception (S0-R1). */
  const quietly = (work: () => void): void => {
    try {
      work();
    } catch {
      // An engine that objects has cost this sitting a sound. It may never cost it a round.
    }
  };

  const applyMasterGain = (rampMs: number): void => {
    quietly(() => {
      engine.setMasterGain(enabled ? volume : 0, rampMs);
    });
  };

  /** Brings every live sound to the gain the current foreground implies. */
  const applyDucking = (): void => {
    for (const sound of live.values()) {
      const wanted = shouldDuck(sound.definition);
      if (wanted === sound.ducked) continue;
      sound.ducked = wanted;
      quietly(() => {
        sound.track.setGain(
          wanted ? sound.fullGain * AUDIO_TUNABLES.duckedGain : sound.fullGain,
          wanted ? AUDIO_TUNABLES.duckAttackMs : AUDIO_TUNABLES.duckReleaseMs,
        );
      });
    }
  };

  /**
   * Takes a sound out of the live set — the one door out, whatever the reason.
   *
   * Every reason passes through here so that releasing a `playToEnd` waiter is structural rather
   * than enumerated: a sound that has left cannot be one somebody is still waiting for. Only a
   * sound that is being cut short is stopped at the engine; one that reached its own end, or one
   * the browser refused, has nothing left to stop.
   */
  const releaseSound = (audioId: AudioId, end: AudioPlaybackEnd): void => {
    const sound = live.get(audioId);
    if (sound === undefined) return;
    live.delete(audioId);
    if (end === 'stopped') {
      quietly(() => {
        sound.track.stop();
      });
    }
    sound.finish?.(end);
  };

  const stopSound = (audioId: AudioId): void => {
    releaseSound(audioId, 'stopped');
  };

  /**
   * Starts one sound, or explains nothing by returning false.
   *
   * Shared by `play` and `playToEnd` so that "may this sound start, and at what level" is decided
   * once. The two differ only in what they do afterwards.
   */
  const startSound = (
    audioId: AudioId,
    definition: AudioDefinition,
    gainScale: number,
    finish?: (end: AudioPlaybackEnd) => void,
  ): boolean => {
    const fullGain = clampGain(categoryGain(definition) * definition.volume * gainScale);
    const ducked = shouldDuck(definition);
    /**
     * Assigned after the request is built, and read only from callbacks the engine fires later.
     *
     * Both callbacks have to identify *this* sound rather than whatever is under the ID by then: a
     * cue that was restarted and then ended, or refused, must not remove the one that replaced it.
     */
    let track: AudioTrack | null = null;
    /** Takes this sound out of the live set, if it is still the one there. */
    const leave = (end: AudioPlaybackEnd): void => {
      if (track === null || live.get(audioId)?.track !== track) return;
      releaseSound(audioId, end);
      notify();
    };
    try {
      track = engine.play({
        audioId,
        source: definition.source,
        loop: definition.loop,
        bus: busFor(definition),
        gain: ducked ? fullGain * AUDIO_TUNABLES.duckedGain : fullGain,
        onEnded: () => {
          leave('ended');
        },
        /**
         * The browser said no after the fact (R1-3). Same door out, different reason — so a caller
         * waiting on this line learns it was refused and can say it another way.
         */
        onRefused: () => {
          leave('refused');
        },
      });
    } catch {
      // The engine threw. Silence — and the readout says so, rather than listing a sound that
      // is not sounding and that `stop` could never stop.
      return false;
    }
    // The engine refused outright: there is no track, so there is nothing to list.
    if (track === null) return false;
    live.set(audioId, {
      definition,
      track,
      fullGain,
      ducked,
      ...(finish === undefined ? {} : { finish }),
    });
    notify();
    return true;
  };

  return {
    get unlocked() {
      return unlocked;
    },
    get enabled() {
      return enabled;
    },
    get volume() {
      return volume;
    },
    get ducked() {
      return isDucked();
    },

    playing() {
      return [...live.keys()];
    },

    subscribe(listener) {
      listeners.add(listener);
      /**
       * The current state, immediately (S0-R1 regression).
       *
       * The stage subscribes when it mounts, which is *after* the grown-up's gesture unlocked
       * audio on the setup screen. A readout that only spoke on the next change showed a muted
       * sitting — where nothing ever starts — as "not unlocked" for the whole mission, because it
       * had been relying on incidental notifications from narration's ducking to tell it anything
       * at all. A readout has to be correct the moment it exists.
       */
      try {
        listener(snapshot());
      } catch {
        // A readout that throws is a readout problem. It must not silence the game.
      }
      return () => {
        listeners.delete(listener);
      };
    },

    loadLibrary(audio) {
      library = audio;
    },

    async unlock() {
      if (disposed || unlocked) return;
      /**
       * A context that refuses to resume leaves the sitting **locked**, not half-unlocked (S0-R1).
       *
       * Reporting an unlock that did not happen would have every later `play` reach an engine that
       * cannot make a sound, and `unlock` is awaited by the setup screen — a rejection here would
       * have escaped as an unhandled rejection on the way into a child's mission.
       */
      try {
        await engine.resume();
      } catch {
        return;
      }
      // Checked again: disposal can land while the engine is resuming, and a sitting that has
      // ended must not come back capable of sound.
      if (disposed) return;
      unlocked = true;
      // The chosen volume may have been set before the gesture; the engine hears it now.
      applyMasterGain(0);
      notify();
    },

    play(audioId) {
      if (disposed || !unlocked) return;
      const definition = library[audioId];
      // A sound the manifest does not declare is silence, not a failure (D-014's rule, applied
      // to audio): a stale ID must never be able to interrupt a mission.
      if (definition === undefined) return;
      // The **Sound** switch governs the sound bus and says so on its label; speech answers to the
      // narration controls instead (A-R1).
      if (!enabled && busFor(definition) === 'sound') return;

      const existing = live.get(audioId);
      if (existing !== undefined) {
        // One sound per ID. A loop already looping is already what was asked for; a cue asked for
        // again starts again rather than stacking a second voice over itself.
        if (!definition.loop) {
          quietly(() => {
            existing.track.restart();
          });
        }
        return;
      }

      startSound(audioId, definition, 1);
    },

    declaredIds() {
      return Object.keys(library);
    },

    async playToEnd(audioId, options) {
      const definition = library[audioId];
      // Every reason a sound cannot start resolves at once — and says it never made a sound, so a
      // caller with another way to be heard can take it. A caller waiting on silence is the S0-R1
      // failure wearing a different hat.
      if (disposed || !unlocked || definition === undefined) return 'refused';
      // As in `play`: muting the music does not mute the mission's voice (A-R1). The autoplay lock
      // above is not a setting and applies to everything.
      if (!enabled && busFor(definition) === 'sound') return 'refused';
      // A loop has no end, so waiting for one is waiting for ever.
      if (definition.loop) return 'refused';

      // The same line asked for again replaces the one still sounding rather than layering over
      // it; the first caller is released by the release path itself.
      stopSound(audioId);

      return await new Promise<AudioPlaybackEnd>((resolve) => {
        if (!startSound(audioId, definition, clampScale(options?.gain ?? 1), resolve)) {
          resolve('refused');
        }
      });
    },

    stop(audioId) {
      stopSound(audioId);
      notify();
    },

    stopAll() {
      for (const audioId of [...live.keys()]) stopSound(audioId);
      notify();
    },

    setEnabled(next) {
      enabled = next;
      // Immediate, not ramped, either way: a grown-up reaching for mute wants silence rather than
      // a fade, and the way back is the same gesture.
      applyMasterGain(0);
      notify();
    },

    setVolume(next) {
      volume = clampGain(next);
      applyMasterGain(0);
      notify();
    },

    claimForeground(priority) {
      const token = Symbol('foreground');
      claims.push({ token, priority });
      applyDucking();
      notify();

      let released = false;
      return () => {
        if (released) return;
        released = true;
        const index = claims.findIndex((claim) => claim.token === token);
        if (index >= 0) claims.splice(index, 1);
        applyDucking();
        notify();
      };
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      unlocked = false;
      for (const audioId of [...live.keys()]) stopSound(audioId);
      claims.length = 0;
      quietly(() => {
        engine.close();
      });
      notify();
      listeners.clear();
    },
  };
}
